import {
  Box,
  Typography,
  Divider,
} from "@mui/material";
import {
  PrivacyTip,
  Block,
  Info,
  Cookie,
  Security,
  ChildCare,
  Gavel,
  Link as LinkIcon,
  Update,
  Email,
} from "@mui/icons-material";
import { Helmet } from "react-helmet-async";
import BackButton from "../components/utils/BackButton";

export default function PrivacyPolicy() {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | MarriageBiodata.live</title>
        <meta
          name="description"
          content="Read the Privacy Policy of MarriageBiodata.live to understand how we handle your data while using our free online marriage biodata generator tool."
        />
        <link
          rel="canonical"
          href="https://www.marriagebiodata.live/privacy-policy"
        />
      </Helmet>

      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://www.marriagebiodata.live",
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Privacy Policy",
              "item": "https://www.marriagebiodata.live/privacy-policy",
            },
          ],
        })}
      </script>

      <Box
        component="main"
        sx={{
          maxWidth: 1000,
          mx: "auto",
          px: { xs: 2, md: 4 },
          py: { xs: 4, md: 6 },
          lineHeight: 1.75,
        }}
      >
        <BackButton fallback="/" />
        {/* Page Header */}
        <Box display="flex" alignItems="center" gap={1} mb={1}>
          <PrivacyTip sx={{ color: "warning.main" }} />
          <Typography variant="h3" fontWeight={700}>
            Privacy Policy
          </Typography>
        </Box>

        <Typography color="text.secondary" paragraph>
          Effective Date: January 2026
        </Typography>

        <Divider sx={{ mb: 4 }} />

        <Typography paragraph>
          MarriageBiodata.live ("we", "our", or "the website") respects your
          privacy and is committed to protecting your personal information. This
          Privacy Policy explains how our free online marriage biodata generator
          tool handles information when you use the website.
        </Typography>

        <Typography paragraph>
          By accessing or using MarriageBiodata.live, you agree to the practices
          described in this policy.
        </Typography>

        {/* Section 1 */}
        <SectionHeader icon={<Block />} title="1. Information We Do Not Collect" />
        <Typography paragraph>
          MarriageBiodata.live does not collect, store, transmit, or process any
          personal data on our servers. All information entered into the biodata
          form, including names, contact details, photos, and personal
          information, remains strictly inside your browser and is never sent to
          our backend or external systems.
        </Typography>

        <Typography paragraph>
          Your biodata is generated locally within your device and can be
          downloaded directly as a PDF file. Once you close the browser or
          refresh the page, your data is removed from memory.
        </Typography>

        {/* Section 2 */}
        <SectionHeader
          icon={<Info />}
          title="2. Information That May Be Collected Automatically"
        />
        <Typography paragraph>
          While we do not collect personal biodata information, certain
          non-personal technical information may be collected automatically by
          third-party services, such as:
        </Typography>

        <Typography component="ul" sx={{ pl: 3 }}>
          <li>Browser type and version</li>
          <li>Device type and operating system</li>
          <li>Approximate location (city or region)</li>
          <li>Pages visited and interaction behavior</li>
          <li>Anonymous usage statistics</li>
        </Typography>

        <Typography paragraph>
          This information is used solely to improve website performance,
          security, and user experience.
        </Typography>

        {/* Section 3 */}
        <SectionHeader
          icon={<Cookie />}
          title="3. Cookies and Advertising"
        />
        <Typography paragraph>
          MarriageBiodata.live may display advertisements served by third-party
          advertising partners such as Google AdSense. These partners may use
          cookies or similar technologies to display relevant ads based on your
          browsing behavior.
        </Typography>

        <Typography paragraph>
          Google may use the DoubleClick cookie to serve ads to users based on
          their visits to this and other websites. Users may opt out of
          personalized advertising by visiting Google Ads Settings.
        </Typography>

        <Typography paragraph>
          We do not control how third-party advertisers collect or use
          information. Please review their respective privacy policies for more
          details.
        </Typography>

        {/* Section 4 */}
        <SectionHeader icon={<Security />} title="4. Data Security" />
        <Typography paragraph>
          Since all biodata processing happens locally in your browser, there is
          no centralized storage of sensitive personal information. This
          architecture significantly reduces the risk of data breaches and
          unauthorized access.
        </Typography>

        <Typography paragraph>
          We implement standard security practices to protect the website
          against malicious activity and vulnerabilities.
        </Typography>

        {/* Section 5 */}
        <SectionHeader icon={<ChildCare />} title="5. Children's Privacy" />
        <Typography paragraph>
          MarriageBiodata.live is not intended for use by children under the age
          of 13. We do not knowingly collect any personal information from
          children.
        </Typography>

        {/* Section 6 */}
        <SectionHeader icon={<Gavel />} title="6. Your Rights and Choices" />
        <Typography paragraph>
          Since we do not store your personal data, there is no personal
          information for us to access, modify, or delete. You maintain full
          control over your data on your device at all times.
        </Typography>

        <Typography paragraph>
          You may control cookies and advertising preferences directly from your
          browser or through third-party advertising platforms.
        </Typography>

        {/* Section 7 */}
        <SectionHeader icon={<LinkIcon />} title="7. External Links" />
        <Typography paragraph>
          Our website may contain links to external websites. We are not
          responsible for the privacy practices or content of those websites.
        </Typography>

        {/* Section 8 */}
        <SectionHeader icon={<Update />} title="8. Policy Updates" />
        <Typography paragraph>
          We may update this Privacy Policy from time to time. Any changes will
          be reflected on this page with a revised effective date.
        </Typography>

        {/* Section 9 */}
        <SectionHeader icon={<Email />} title="9. Contact Information" />
        <Typography paragraph>
          If you have any questions about this Privacy Policy or the website, you
          may contact us at:
        </Typography>

        <Typography fontWeight={600}>
          deepfunde1995@gmail.com
        </Typography>
      </Box>
    </>
  );
}

/* ---------- Reusable Section Header ---------- */
function SectionHeader({ icon, title }) {
  return (
    <Box display="flex" alignItems="center" gap={1} mt={4} mb={1}>
      <Box sx={{ color: "warning.main" }}>{icon}</Box>
      <Typography variant="h5" fontWeight={600}>
        {title}
      </Typography>
    </Box>
  );
}
